const buildYourOwnManager = {
  selectionState: new WeakMap(),
  // Read the active in-page selections for a BYO section.
  readSelections: function (byoSection) {
    const savedSelections = this.selectionState.get(byoSection);

    if ( savedSelections ) {
      return savedSelections;
    }

    const emptySelections = {};
    return emptySelections;
  },
  // Keep the current selections in memory while the BYO page remains open.
  writeSelections: function (byoSection, selections) {
    this.selectionState.set(byoSection, selections);
  },
  // Format a price in cents using the shop's configured money format.
  formatMoney: function (cents) {
    const format = window.themeVariables.settings.moneyFormat;
    const placeholder = format.match(/{{\s*(\w+)\s*}}/);

    if ( !placeholder ) {
      const fallbackMoney = (Number(cents || 0) / 100).toFixed(2);
      return fallbackMoney;
    }

    // Format the numeric portion of a money value with the requested separators.
    const formatNumber = ( value, decimals, thousands, decimal ) => {
      const number = (Number(value || 0) / 100).toFixed(decimals);
      const parts = number.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const formattedNumber = parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
      return formattedNumber;
    };
    const formats = {
      amount: formatNumber(cents, 2, ',', '.'),
      amount_no_decimals: formatNumber(cents, 0, ',', '.'),
      amount_with_comma_separator: formatNumber(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: formatNumber(cents, 0, '.', ','),
      amount_with_apostrophe_separator: formatNumber(cents, 2, "'", '.'),
      amount_no_decimals_with_space_separator: formatNumber(cents, 0, ' ', '.')
    };

    const formattedMoney = format.replace(placeholder[0], formats[placeholder[1]] || formats.amount);
    const decoder = document.createElement('textarea');
    decoder.innerHTML = formattedMoney;
    const decodedMoney = decoder.value;
    return decodedMoney;
  },
  // Update the bundle total, ATC state, review area, and dropdown progression.
  updateBundleTotal: function (byoSection, selections = this.readSelections(byoSection), selectedSlot = '', deferDropdownColorSync = false) {
    const total = this.calculateBundleTotal(selections);
    let hasOption = false;

    for (const slot in selections) {
      if (slot.startsWith('option-')) {
        hasOption = true;
      }
    }

    const totalElement = byoSection.querySelector('[data-byo-bundle-total]');
    const addBundleButton = byoSection.querySelector('[data-byo-add-bundle-cart]');
    const hasChain = Boolean(selections.chain);

    if ( totalElement ) {
      totalElement.textContent = this.formatMoney(total);
    }
    if ( addBundleButton ) {
      addBundleButton.disabled = !(hasChain && hasOption);
    }
    this.renderReview(byoSection, selections);
    const openedDropdown = this.updateDropdownProgress(byoSection, selections, selectedSlot, deferDropdownColorSync);
    return openedDropdown;
  },
  // Calculate the complete bundle price, including personalization add-ons.
  calculateBundleTotal: function (selections) {
    let total = 0;

    for (const slot in selections) {
      total += Number(selections[slot].priceCents || 0);
    }

    return total;
  },
  // Render the selected product images and remove buttons in the review area.
  renderReview: function (byoSection, selections) {
    const reviewPanel = byoSection.querySelector('.byo-actions__review-panel');

    if ( !reviewPanel ) {
      return
    }

    reviewPanel.replaceChildren();

    const orderedSelections = Object.values(selections);
    orderedSelections.sort((first, second) => {
      if (first.slot === 'chain') {
        return -1;
      }
      if (second.slot === 'chain') {
        return 1;
      }
      const sortPosition = first.slot.localeCompare(second.slot, undefined, { numeric: true });
      return sortPosition;
    });

    for (const selection of orderedSelections) {
      const item = document.createElement('div');
      const openButton = document.createElement('button');
      const image = document.createElement('img');
      const removeButton = document.createElement('button');

      item.className = 'byo-actions__review-item';
      item.dataset.byoReviewItem = selection.slot;

      openButton.type = 'button';
      openButton.className = 'byo-actions__review-open';
      openButton.dataset.byoReviewOpen = selection.slot;
      openButton.setAttribute('aria-label', `Edit ${selection.productTitle}`);

      image.className = 'byo-actions__review-image';
      image.src = selection.productImage;
      image.alt = selection.productTitle;
      image.loading = 'lazy';

      removeButton.type = 'button';
      removeButton.className = 'byo-actions__review-remove link';
      removeButton.dataset.byoReviewRemove = selection.slot;
      removeButton.textContent = byoSection.dataset.byoRemoveLabel;

      openButton.append(image);
      item.append(openButton, removeButton);
      reviewPanel.append(item);
    }
  },
  // Open the selected product modal from its review-bundle image.
  openReviewModal: function (byoSection, slot) {
    const selections = this.readSelections(byoSection);
    const selection = selections[slot];

    if ( !selection ) {
      return
    }

    const escapedSlot = CSS.escape(slot);
    const escapedProductId = CSS.escape(String(selection.productId));
    const dropdown = byoSection.querySelector(`[data-byo-dropdown][data-byo-slot="${escapedSlot}"]`);
    const productCard = dropdown?.querySelector(`[data-byo-product-card][data-product-id="${escapedProductId}"]`);
    const modal = productCard ? document.getElementById(productCard.dataset.byoModalId) : null;

    if ( !productCard || !modal ) {
      return
    }

    this.selectCardVariant(productCard, selection.variantId);
    this.updateModalActionLabel(productCard, modal);
    const showPromise = modal.show();

    showPromise.then(() => {
      this.selectCardVariant(productCard, selection.variantId);
      this.updateModalActionLabel(productCard, modal);
    });

    const personalization = modal.querySelector('[data-byo-personalization]');

    if ( personalization && window.byoPersonalizationManager ) {
      window.byoPersonalizationManager.loadForm(personalization);
    }
  },
  // Keep the review panel expanded on desktop and controlled by its toggle on mobile.
  syncReviewPanel: function (byoSection) {
    const toggle = byoSection.querySelector('.byo-actions__review-toggle');
    const panel = byoSection.querySelector('.byo-actions__review-panel');

    if ( !toggle || !panel ) {
      return
    }

    panel.hidden = window.matchMedia('(min-width: 1000px)').matches ? false : toggle.getAttribute('aria-expanded') !== 'true';
  },
  // Store the selected product details in the dropdown's hidden HTML element.
  setSelectionData: function (dropdown, selection) {
    const selectionElement = dropdown.querySelector('[data-byo-selection]');

    if ( !selectionElement ) {
      return
    }

    selectionElement.dataset.selection = JSON.stringify(selection);
    selectionElement.dataset.productId = selection.productId;
    selectionElement.dataset.variantId = selection.variantId;
    selectionElement.dataset.productTitle = selection.productTitle;
    selectionElement.dataset.productUrl = selection.productUrl;
    selectionElement.dataset.productImage = selection.productImage;
  },
  // Remove stored product details from the dropdown's hidden HTML element.
  clearSelectionData: function (dropdown) {
    const selectionElement = dropdown.querySelector('[data-byo-selection]');

    if ( !selectionElement ) {
      return
    }

    for ( let attributeIndex = selectionElement.attributes.length - 1; attributeIndex >= 0; attributeIndex -= 1 ) {
      const attributeName = selectionElement.attributes[attributeIndex].name;

      if ( attributeName.startsWith('data-') && attributeName !== 'data-byo-selection' ) {
        selectionElement.removeAttribute(attributeName);
      }
    }
  },
  // Replace a BYO card image with the image for its current selected variant.
  setCardImage: function (productCard, imageUrl) {
    const image = productCard?.querySelector('.product-card__figure .product-card__media img');

    if ( !image || !imageUrl ) {
      return
    }

    image.src = imageUrl;
    image.removeAttribute('srcset');
  },
  // Read the selected color value and its swatch style from a product modal.
  getSelectedColorData: function (modal) {
    const colorInput = modal?.querySelector('[data-byo-color-option]:checked');
    let colorData = null;

    if ( colorInput ) {
      const colorLabel = modal.querySelector(`label[for="${CSS.escape(colorInput.id)}"]`);
      colorData = {
        value: colorInput.value,
        swatchStyle: colorLabel?.getAttribute('style') || ''
      };
    }

    return colorData;
  },
  // Restore one exact saved variant before reopening a selected product modal.
  selectCardVariant: function (productCard, variantId) {
    const modal = productCard ? document.getElementById(productCard.dataset.byoModalId) : null;
    const quickAdd = modal?.querySelector('upsell-quick-add');
    const variantInput = modal?.querySelector('[data-upsell-variant-id]');
    const variantInputValue = variantInput?.value || '';
    let variants = quickAdd?.variantData || [];

    if ( !modal || !quickAdd || !variantId ) {
      return
    }

    if ( variantInputValue === variantId ) {
      return
    }

    if ( variants.length === 0 ) {
      try {
        variants = JSON.parse(modal.querySelector('[data-upsell-variants]')?.textContent || '[]');
      } catch (error) {
        variants = [];
      }
    }

    let selectedVariant = null;

    for ( const variant of variants ) {
      if ( variant.id === variantId ) {
        selectedVariant = variant;
        break;
      }
    }

    if ( selectedVariant && typeof quickAdd.selectVariant === 'function' ) {
      quickAdd.selectVariant(selectedVariant);

      const selectedOptionInput = modal.querySelector('[data-byo-color-option]:checked') || modal.querySelector('[data-option-position]:checked');

      if ( selectedOptionInput ) {
        selectedOptionInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  },
  // Select the chain color, or the first available color, in one product modal.
  preselectCardColor: function (productCard, selectedColor) {
    const modal = productCard ? document.getElementById(productCard.dataset.byoModalId) : null;
    const quickAdd = modal?.querySelector('upsell-quick-add');
    const colorInputs = modal?.querySelectorAll('[data-byo-color-option]') || [];

    if ( !modal || !quickAdd || colorInputs.length === 0 ) {
      return
    }

    let colorInput = null;
    const lowerSelectedColor = selectedColor.trim().toLowerCase();

    for ( const possibleColorInput of colorInputs ) {
      const lowerPossibleColorInput = possibleColorInput.value.trim().toLowerCase();
      if ( lowerPossibleColorInput === lowerSelectedColor ) {
        colorInput = possibleColorInput;
        break;
      }
    }

    if ( !colorInput ) {
      colorInput = colorInputs[0];
    }

    let variants = quickAdd.variantData || [];

    if ( variants.length === 0 ) {
      try {
        variants = JSON.parse(modal.querySelector('[data-upsell-variants]')?.textContent || '[]');
      } catch (error) {
        variants = [];
      }
    }

    const optionIndex = Number(colorInput.dataset.optionPosition) - 1;
    let selectedVariant = null;

    for ( const variant of variants ) {
      const variantColor = String(variant.options?.[optionIndex] || '').trim().toLowerCase();

      if ( variant.available && variantColor === colorInput.value.trim().toLowerCase() ) {
        selectedVariant = variant;
        break;
      }
    }

    if ( !selectedVariant ) {
      for ( const variant of variants ) {
        if ( variant.available ) {
          selectedVariant = variant;
          break;
        }
      }
    }

    if ( !selectedVariant ) {
      selectedVariant = variants[0] || null;
    }

    if ( !selectedVariant ) {
      return
    }

    if ( typeof quickAdd.selectVariant === 'function' ) {
      quickAdd.selectVariant(selectedVariant);
    }

    const selectedColorValue = selectedVariant.options?.[optionIndex] || colorInput.value;
    const selectedColorInputs = modal.querySelectorAll('[data-byo-color-option]');

    for ( const possibleColorInput of selectedColorInputs ) {
      possibleColorInput.checked = possibleColorInput.value === selectedColorValue;
    }

    colorInput.dispatchEvent(new Event('change', { bubbles: true }));

    const selectedImage = this.getSelectedImage(modal, selectedVariant.id, productCard.dataset.productImage);
    productCard.dataset.byoChainImage = selectedImage;
    this.setCardImage(productCard, selectedImage);
  },
  // Update only a product card image for the matching chain color.
  syncCardImageToChainColor: function (productCard, selectedColor) {
    const modal = productCard ? document.getElementById(productCard.dataset.byoModalId) : null;
    const colorInputs = modal?.querySelectorAll('[data-byo-color-option]') || [];
    const lowerSelectedColor = selectedColor.trim().toLowerCase();

    if ( !modal || colorInputs.length === 0 || !lowerSelectedColor ) {
      return
    }

    let colorInput = null;

    for ( const possibleColorInput of colorInputs ) {
      if ( possibleColorInput.value.trim().toLowerCase() === lowerSelectedColor ) {
        colorInput = possibleColorInput;
        break;
      }
    }

    if ( !colorInput ) {
      colorInput = colorInputs[0];
    }

    let variants = [];

    try {
      variants = JSON.parse(modal.querySelector('[data-upsell-variants]')?.textContent || '[]');
    } catch (error) {
      variants = [];
    }

    const optionIndex = Number(colorInput.dataset.optionPosition) - 1;
    let selectedVariant = null;

    for ( const variant of variants ) {
      const variantOption = variant.options?.[optionIndex] || "";
      const variantColor = variantOption.trim().toLowerCase();

      if ( variant.available && variantColor === colorInput.value.trim().toLowerCase() ) {
        selectedVariant = variant;
        break;
      }
    }

    if ( !selectedVariant ) {
      for ( const variant of variants ) {
        if ( variant.available ) {
          selectedVariant = variant;
          break;
        }
      }
    }

    if ( !selectedVariant ) {
      return
    }

    const selectedImage = this.getSelectedImage(modal, selectedVariant.id, productCard.dataset.productImage);
    productCard.dataset.byoChainImage = selectedImage;
    this.setCardImage(productCard, selectedImage);
  },
  // Store a selected chain color for lazy syncing
  applyChainColor: function (byoSection, selectedColor) {
    const dropdowns = byoSection.querySelectorAll('[data-byo-dropdown]');
    const byoTemplate = byoSection.querySelector('.byo');

    if ( byoTemplate ) {
      byoTemplate.setAttribute('data-byo-chain-color', selectedColor);
    }

    for ( const dropdown of dropdowns ) {
      const slot = dropdown.dataset.byoSlot;

      if ( slot === 'chain' ) {
        continue;
      }

      dropdown.setAttribute('data-byo-chain-color-synced', 'false');
    }
  },
  // Lazily update one non-chain dropdown's card images once per selected chain color.
  syncDropdownChainColorImages: function (byoSection, dropdown) {
    const enforceColorMatch = byoSection.getAttribute('data-force-charm-colors-to-match-chain') === 'true';
    const byoTemplate = byoSection.querySelector('.byo');
    const selectedColor = byoTemplate?.getAttribute('data-byo-chain-color') || '';

    if ( !enforceColorMatch || !selectedColor || dropdown.dataset.byoSlot === 'chain' ) {
      return
    }

    if ( dropdown.getAttribute('data-byo-chain-color-synced') === 'true' ) {
      return
    }

    const productCards = dropdown.querySelectorAll('[data-byo-product-card]');

    for ( const productCard of productCards ) {
      if ( productCard.classList.contains('is-byo-selected') ) {
        continue;
      }

      this.syncCardImageToChainColor(productCard, selectedColor);
    }

    dropdown.setAttribute('data-byo-chain-color-synced', 'true');
  },
  // Restore all cards in a dropdown to their unselected state.
  resetCards: function (dropdown) {
    const cards = dropdown.querySelectorAll('[data-byo-product-card]');

    for (const card of cards) {
      card.classList.remove('is-byo-selected', 'is-byo-disabled');
      card.removeAttribute('aria-disabled');
      card.querySelector('[data-byo-product-add]')?.removeAttribute('disabled');
      card.querySelector('[data-byo-remove]')?.remove();
      this.setCardImage(card, card.dataset.byoChainImage || card.dataset.productImage);
    }
  },
  // Apply the selected and disabled visual states to a dropdown's product cards.
  applySelection: function (byoSection, dropdown, selection, selectedCard) {
    dropdown.classList.add('has-selection');

    const cards = dropdown.querySelectorAll('[data-byo-product-card]');

    for (const card of cards) {
      const isSelected = card === selectedCard;
      const addButton = card.querySelector('[data-byo-product-add]');
      const removeButton = card.querySelector('[data-byo-remove]');

      card.classList.toggle('is-byo-selected', isSelected);
      card.classList.toggle('is-byo-disabled', !isSelected);
      card.setAttribute('aria-disabled', String(!isSelected));
      removeButton?.remove();

      if ( isSelected ) {
        addButton?.removeAttribute('disabled');
        this.setCardImage(card, selection.productImage);
      } else {
        addButton?.setAttribute('disabled', '');
        this.setCardImage(card, card.dataset.byoChainImage || card.dataset.productImage);
      }
    }

    if ( selectedCard ) {
      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'byo-product-card__remove link';
      removeButton.dataset.byoRemove = '';
      removeButton.textContent = byoSection.dataset.byoRemoveLabel;
      selectedCard.append(removeButton);
    }

    const selectedTitle = dropdown.querySelector('[data-byo-selected-title]');

    if ( selectedTitle ) {
      selectedTitle.replaceChildren();

      if ( dropdown.dataset.byoSlot === 'chain' && selection.selectedColor ) {
        const selectedSwatch = document.createElement('span');
        selectedSwatch.className = 'byo__selected-swatch';
        selectedSwatch.setAttribute('aria-hidden', 'true');

        if ( selection.selectedColorSwatch ) {
          selectedSwatch.setAttribute('style', selection.selectedColorSwatch);
        }

        selectedTitle.append(selectedSwatch);
      }

      const selectedTitleText = document.createElement('span');
      selectedTitleText.textContent = selection.productTitle;
      selectedTitle.append(selectedTitleText);
      selectedTitle.hidden = false;
    }

    this.setSelectionData(dropdown, selection);
  },
  // Activate the collection tab that contains a restored selected product.
  activateSelectedCollection: function (selectedCard, collectionHandle) {
    const carousel = selectedCard?.closest('featured-collections-carousel');
    const snippet = carousel?.closest('.byo-collection-snippet');

    if ( !carousel || !snippet ) {
      return
    }

    const panels = carousel.querySelectorAll(':scope > .featured-collections-carousel__item');
    let selectedIndex = -1;

    for ( let i = 0; i < panels.length; i += 1 ) {
      if ( panels[i].dataset.collectionHandle === collectionHandle ) {
        selectedIndex = i;
        break;
      }
    }

    if ( selectedIndex < 0 ) {
      return
    }

    const navigationButtons = snippet.querySelectorAll('.byo-collection-snippet__navigation > button');

    for ( let i = 0; i < navigationButtons.length; i += 1 ) {
      navigationButtons[i].setAttribute('aria-current', String(i === selectedIndex));
    }

    if ( typeof carousel.select === 'function' ) {
      carousel.select(selectedIndex, { instant: true });
    } else {
      for ( let i = 0; i < panels.length; i += 1 ) {
        panels[i].classList.toggle('is-selected', i === selectedIndex);
      }
    }
  },
  // Clear one dropdown selection and optionally update persistent storage.
  clearSelection: function (byoSection, dropdown, updateStorage = true) {
    this.resetCards(dropdown);
    this.clearSelectionData(dropdown);
    dropdown.classList.remove('has-selection');

    const selectedTitle = dropdown.querySelector('[data-byo-selected-title]');

    if ( selectedTitle ) {
      selectedTitle.textContent = '';
      selectedTitle.hidden = true;
    }

    if ( updateStorage ) {
      const selections = this.readSelections(byoSection);
      const dropdownSlot = dropdown.dataset.byoSlot;
      const remainingSelections = {};

      for ( const selectionSlot in selections ) {
        if ( selectionSlot !== dropdownSlot ) {
          remainingSelections[selectionSlot] = selections[selectionSlot];
        }
      }

      if ( dropdown.dataset.byoSlot === 'chain' ) {
        byoSection.querySelector('.byo')?.removeAttribute('data-byo-chain-color');
      }

      this.writeSelections(byoSection, remainingSelections);
      this.updateBundleTotal(byoSection, remainingSelections);
    }
  },
  // Collapse a BYO product dropdown.
  closeDropdown: function (dropdown) {
    const toggle = dropdown.querySelector('[data-byo-toggle]');
    const panel = toggle ? document.getElementById(toggle.getAttribute('aria-controls')) : null;

    if ( !toggle || !panel ) {
      return
    }

    toggle.setAttribute('aria-expanded', 'false');
    panel.hidden = true;
  },
  // Expand a BYO product dropdown.
  openDropdown: function (dropdown, shouldSyncChainColor = true) {
    const toggle = dropdown.querySelector('[data-byo-toggle]');
    const panel = toggle ? document.getElementById(toggle.getAttribute('aria-controls')) : null;
    const byoSection = dropdown.closest('[data-byo-section]');

    if ( !toggle || !panel ) {
      return
    }

    if ( byoSection && shouldSyncChainColor ) {
      this.syncDropdownChainColorImages(byoSection, dropdown);
    }

    toggle.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
  },
  // Collapse selected dropdowns and open the next dropdown without a selection.
  updateDropdownProgress: function (byoSection, selections, selectedSlot = '', deferDropdownColorSync = false) {
    const dropdowns = byoSection.querySelectorAll('[data-byo-dropdown]');
    const unselectedDropdowns = [];
    let selectedIndex = -1;

    for (let i = 0; i < dropdowns.length; i += 1) {
      const dropdown = dropdowns[i];

      this.closeDropdown(dropdown);

      if ( !selections[dropdown.dataset.byoSlot] ) {
        unselectedDropdowns.push(dropdown);
      }

      if ( dropdown.dataset.byoSlot === selectedSlot ) {
        selectedIndex = i;
      }
    }

    if ( unselectedDropdowns.length === 0 ) {
      return null
    }

    let nextDropdown = null;

    if ( selectedIndex >= 0 ) {
      for (let i = selectedIndex + 1; i < dropdowns.length; i += 1) {
        const dropdown = dropdowns[i];

        if ( !selections[dropdown.dataset.byoSlot] ) {
          nextDropdown = dropdown;
          break;
        }
      }
    }

    const dropdownToOpen = nextDropdown || unselectedDropdowns[0];
    this.openDropdown(dropdownToOpen, !deferDropdownColorSync);
    return dropdownToOpen;
  },
  // Find the image associated with the selected variant in a product modal.
  getSelectedImage: function (modal, variantId, fallbackImage) {
    try {
      const variants = JSON.parse(modal.querySelector('[data-upsell-variants]')?.textContent || '[]');
      let selectedVariant = null;

      for (const variant of variants) {
        if ( String(variant.id) === String(variantId) ) {
          selectedVariant = variant;
          break
        }
      }

      const image = selectedVariant?.featured_media_id
        ? modal.querySelector(`[data-media-id="${CSS.escape(String(selectedVariant.featured_media_id))}"] img`)
        : null;

      const selectedImage = image?.currentSrc || image?.src || fallbackImage;
      return selectedImage;
    } catch (error) {
      return fallbackImage;
    }
  },
  // Build the complete bundle selection object from the open product modal.
  buildSelection: function (addToBundleButton) {
    const modal = addToBundleButton.closest('x-modal');
    const productCard = modal?.dataset.byoOriginCardId ? document.getElementById(modal.dataset.byoOriginCardId) : addToBundleButton.closest('[data-byo-product-card]');
    const dropdown = productCard?.closest('[data-byo-dropdown]');
    const variantInput = modal?.querySelector('[data-upsell-variant-id]');
    const optionInputs = modal?.querySelectorAll('[data-option-position]:checked') || [];
    const options = [];
    const selectedColorData = this.getSelectedColorData(modal);

    for (const input of optionInputs) {
      options.push({
        name: input.closest('fieldset')?.querySelector('legend')?.textContent.trim() || '',
        position: Number(input.dataset.optionPosition),
        value: input.value
      });
    }

    const variantId = variantInput?.value || '';
    let selectedVariant = null;
    let personalizationSelection = null;

    try {
      const variants = JSON.parse(modal?.querySelector('[data-upsell-variants]')?.textContent || '[]');

      for (const variant of variants) {
        if ( String(variant.id) === String(variantId) ) {
          selectedVariant = variant;
          break
        }
      }
    } catch (error) {
      selectedVariant = null;
    }

    if ( !modal || !productCard || !dropdown || !variantId ) {
      return null;
    }

    const personalization = modal.querySelector('[data-byo-personalization]');

    if ( personalization?.dataset.byoPersonalizationReady === 'true' && window.byoPersonalizationManager ) {
      personalizationSelection = window.byoPersonalizationManager.getFormSelection(personalization);
    }

    let personalizationPriceCents = 0;

    if ( personalizationSelection ) {
      for ( const addOn of personalizationSelection.addOns ) {
        personalizationPriceCents += Number(addOn.priceCents || 0);
      }
    }

    const basePriceCents = Number(selectedVariant?.price || 0);

    const selection = {
      dropdown,
      modal,
      productCard,
      details: {
        slot: dropdown.dataset.byoSlot,
        dropdownLabel: dropdown.querySelector('.byo__toggle-copy > span')?.textContent.trim() || '',
        productId: productCard.dataset.productId,
        variantId,
        productTitle: productCard.dataset.productTitle,
        productUrl: productCard.dataset.productUrl,
        productImage: this.getSelectedImage(modal, variantId, productCard.dataset.productImage),
        collectionHandle: productCard.dataset.byoCollectionHandle,
        price: modal.querySelector('[data-upsell-price-wrapper]')?.textContent.replace(/\s+/g, ' ').trim() || '',
        basePriceCents,
        priceCents: basePriceCents + personalizationPriceCents,
        options,
        selectedColor: selectedColorData?.value || '',
        selectedColorSwatch: selectedColorData?.swatchStyle || '',
        personalization: personalizationSelection
      }
    };

    return selection
  },
  // Show Update item when reopening a modal for an existing dropdown selection.
  updateModalActionLabel: function (productCard, modal) {
    const dropdown = productCard?.closest('[data-byo-dropdown]');
    const selectionElement = dropdown?.querySelector('[data-byo-selection]');
    const addToBundleButton = modal?.querySelector('[data-byo-add-to-bundle]');

    if ( !addToBundleButton ) {
      return
    }

    let savedProductId = '';

    try {
      const savedSelection = JSON.parse(selectionElement?.dataset.selection || '{}');
      savedProductId = String(savedSelection.productId || '');
    } catch (error) {
      savedProductId = '';
    }

    const isExistingSelection = savedProductId === String(productCard.dataset.productId || '');
    const availableLabel = addToBundleButton.dataset.availableText || 'Add to bundle';

    const updateLabel = addToBundleButton.dataset.byoUpdateText || availableLabel;
    addToBundleButton.textContent = isExistingSelection ? updateLabel : availableLabel;
  },
  // Restore valid saved selections and their collection tabs after page load.
  restoreSelections: function (byoSection, deferDropdownColorSync = false) {
    const selections = this.readSelections(byoSection);
    const restoredSelections = {};
    const enforceColorMatch = byoSection.getAttribute('data-force-charm-colors-to-match-chain') === 'true';

    if ( selections.chain?.selectedColor && enforceColorMatch ) {
      this.applyChainColor(byoSection, selections.chain.selectedColor);
    }

    for ( const selection of Object.values(selections) ) {
      const slot = CSS.escape(selection.slot);
      const productId = CSS.escape(String(selection.productId));
      const collectionSelector = selection.collectionHandle ? `[data-byo-collection-handle="${CSS.escape(selection.collectionHandle)}"]` : '';
      const dropdown = byoSection.querySelector(`[data-byo-dropdown][data-byo-slot="${slot}"]`);
      let selectedCard = dropdown?.querySelector(`[data-byo-product-card][data-product-id="${productId}"]${collectionSelector}`);

      if ( !selectedCard ) {
        selectedCard = dropdown?.querySelector(`[data-byo-product-card][data-product-id="${productId}"]`);

        if ( selectedCard ) {
          selection.collectionHandle = selectedCard.dataset.byoCollectionHandle;
        }
      }

      if ( dropdown && selectedCard ) {
        selection.collectionHandle ||= selectedCard.dataset.byoCollectionHandle;
        restoredSelections[selection.slot] = selection;
        this.activateSelectedCollection(selectedCard, selection.collectionHandle);
        this.applySelection(byoSection, dropdown, selection, selectedCard);
      }
    }

    this.writeSelections(byoSection, restoredSelections);
    const openedDropdown = this.updateBundleTotal(byoSection, restoredSelections, '', deferDropdownColorSync);
    return openedDropdown;
  },
  // Replace the modal action label with a spinner while a selection is being applied.
  setModalActionLoading: function (addToBundleButton, isLoading) {
    if ( isLoading ) {
      addToBundleButton.dataset.byoLoadingLabel = addToBundleButton.textContent.trim();
      addToBundleButton.textContent = '';
      addToBundleButton.disabled = true;
      addToBundleButton.setAttribute('aria-busy', 'true');
      addToBundleButton.setAttribute('aria-label', addToBundleButton.dataset.byoLoadingLabel);

      const spinner = document.createElement('span');
      spinner.className = 'byo-quick-add-modal__action-spinner';
      spinner.setAttribute('aria-hidden', 'true');
      addToBundleButton.append(spinner);
      return;
    }

    const buttonLabel = addToBundleButton.dataset.byoLoadingLabel || addToBundleButton.dataset.availableText || 'Add to bundle';
    addToBundleButton.textContent = buttonLabel;
    addToBundleButton.disabled = false;
    addToBundleButton.removeAttribute('aria-busy');
    addToBundleButton.removeAttribute('aria-label');
    addToBundleButton.removeAttribute('data-byo-loading-label');
  },
  // Allow the browser to paint a loading state before starting synchronous selection work.
  waitForNextPaint: function () {
    const paintPromise = new Promise((resolve) => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(resolve);
      });
    });

    return paintPromise;
  },
  // Save a product selected through an Add to bundle modal action.
  handleAddToBundle: async function (event) {
    const addToBundleButton = event.target.closest('[data-byo-add-to-bundle]');

    if ( !addToBundleButton || addToBundleButton.getAttribute('aria-busy') === 'true' ) {
      return
    }

    const modal = addToBundleButton.closest('x-modal');
    const personalization = modal?.querySelector('[data-byo-personalization]');

    if ( personalization && window.byoPersonalizationManager ) {
      const isValid = window.byoPersonalizationManager.validateForm(personalization);

      if ( !isValid || addToBundleButton.disabled ) {
        return;
      }
    }

    const selection = this.buildSelection(addToBundleButton);
    const byoSection = selection?.dropdown.closest('[data-byo-section]');

    if ( !selection || !byoSection ) {
      return
    }

    this.setModalActionLoading(addToBundleButton, true);
    await this.waitForNextPaint();

    try {
      const selections = this.readSelections(byoSection);
      selections[selection.details.slot] = selection.details;
      this.writeSelections(byoSection, selections);
      const formColorMatch = byoSection.getAttribute('data-force-charm-colors-to-match-chain') === 'true';

      if ( selection.details.slot === 'chain' && formColorMatch ) {
        this.applyChainColor(byoSection, selection.details.selectedColor);
      }

      this.updateBundleTotal(byoSection, selections, selection.details.slot);
      this.applySelection(byoSection, selection.dropdown, selection.details, selection.productCard);

      this.closeDropdown(selection.dropdown);

      byoSection.dispatchEvent(new CustomEvent('byo:bundle-add', {
        bubbles: true,
        detail: selection.details
      }));

      await selection.modal.hide();
    } finally {
      this.setModalActionLoading(addToBundleButton, false);
    }
  },
  // Build the local storage key used to restore an existing cart group for editing.
  getBundleStorageKey: function (bundleId) {
    const bundleStorageKey = `tiny-tags:byo-bundle:${bundleId}`;
    return bundleStorageKey;
  },
  // Save the selections used to create one cart group.
  writeBundleSelections: function (bundleId, selections) {
    try {
      localStorage.setItem(this.getBundleStorageKey(bundleId), JSON.stringify(selections));
    } catch (error) {
      // Editing remains unavailable if browser storage cannot be used.
    }
  },
  // Read the selections used to create one cart group.
  readBundleSelections: function (bundleId) {
    try {
      const selections = JSON.parse(localStorage.getItem(this.getBundleStorageKey(bundleId)) || '{}');
      return selections;
    } catch (error) {
      const emptySelections = {};
      return emptySelections;
    }
  },
  // Return the cart group explicitly requested by an Edit link.
  getRequestedEditingBundleId: function () {
    const url = new URL(window.location.href);
    const bundleId = url.searchParams.get('edit_byo_bundle') || '';
    return bundleId;
  },
  // Only an explicit cart Edit URL can put the page into edit mode.
  getEditingBundleId: function () {
    const requestedBundleId = this.getRequestedEditingBundleId();
    const bundleId = requestedBundleId;
    return bundleId;
  },
  // Make the requested cart group's saved data the active section data before rendering cards.
  prepareEditingSelections: function (byoSection) {
    const bundleId = this.getEditingBundleId();

    if ( !bundleId ) {
      return
    }

    const selections = this.readBundleSelections(bundleId);
    let selectionCount = 0;

    for (const slot in selections) {
      selectionCount += 1;
    }

    if ( selectionCount > 0 ) {
      this.writeSelections(byoSection, selections);
    }
  },
  // Clear all selections in one BYO section.
  resetSection: function (byoSection) {
    const dropdowns = byoSection.querySelectorAll('[data-byo-dropdown]');
    const emptySelections = {};

    for (const dropdown of dropdowns) {
      this.clearSelection(byoSection, dropdown, false);
    }

    byoSection.querySelector('.byo')?.removeAttribute('data-byo-chain-color');

    this.writeSelections(byoSection, emptySelections);

    this.updateBundleTotal(byoSection, emptySelections);
  },
  // Build Shopify cart lines linked by a private BYO group ID. Products with
  // back engraving or inline gemstones also use the cart transformer bundle properties.
  buildCartItems: function (selections, existingBundleId = '', byoPagePath = '', byoCartTitle = '') {
    const bundleId = existingBundleId || String(Date.now());
    const sourcePagePath = byoPagePath || window.location.pathname;
    const cartTitle = byoCartTitle || 'Build Your Own';
    const items = [];
    let bundlePropertyString = '_bundle';
    let bundleNamePropertyString = '_bundle_name';
    let bundleLabelString = '_bundle_item_label';
    for ( const slot in selections ) {
      const selection = selections[slot];
      const properties = {
        _byo_bundle: bundleId,
        _byo_page: sourcePagePath,
        _byo_title: cartTitle
      };

      if ( slot === 'chain' ) {
        properties._byo_chain = 'true';
      }

      const personalizationProperties = selection.personalization?.properties || {};

      for (const propertyName in personalizationProperties) {
        properties[propertyName] = personalizationProperties[propertyName];
      }

      const mainItem = {
        id: Number(selection.variantId),
        quantity: 1,
        properties,
        byoSourceSlot: slot
      };

      const addOns = selection.personalization?.addOns || [];
      let hasBackEngraving = false;
      let hasBundledGemstones = false;

      for ( const addOn of addOns ) {
        if ( addOn.type === 'back-engraving' ) {
          hasBackEngraving = true;
        }

        if ( addOn.type === 'gemstone' && addOn.bundleWithProduct === true ) {
          hasBundledGemstones = true;
        }
      }

      // The cart transformer merges the main product with its back engraving and
      // any gemstones inserted directly into a personalized text field.
      // _byo_bundle remains on both lines so the complete BYO cart group still works.
      const hasTransformerBundle = hasBackEngraving || hasBundledGemstones;
      let transformerBundleId = '';
      let parentVariantId = '';

      if ( hasTransformerBundle ) {
        transformerBundleId = `${bundleId}-${slot}-${selection.variantId}`;
        parentVariantId = `gid://shopify/ProductVariant/${selection.variantId}`;
        mainItem.properties[bundlePropertyString] = transformerBundleId;
        mainItem.properties[bundleNamePropertyString] = selection.productTitle;
        mainItem.properties[bundleLabelString] = selection.productTitle;
        mainItem.properties._type = 'CORE';
        mainItem.properties._parent_variant_id = parentVariantId;
      }

      const backEngravingItems = [];
      const otherAddOnItems = [];
      let bundledGemstoneCount = 0;

      for ( const addOn of addOns ) {
        const addOnProperties = {
          _byo_bundle: bundleId,
          _byo_page: sourcePagePath,
          _byo_title: cartTitle
        };
        const shouldBundleAddOn = addOn.type === 'back-engraving' || (addOn.type === 'gemstone' && addOn.bundleWithProduct === true);

        if ( shouldBundleAddOn ) {
          addOnProperties[bundlePropertyString] = transformerBundleId;
          addOnProperties[bundleNamePropertyString] = selection.productTitle;
          addOnProperties._type = 'ADDON';
          addOnProperties._parent_variant_id = parentVariantId;

          if ( addOn.type === 'back-engraving' ) {
            addOnProperties[bundleLabelString] = addOn.label || 'Back Engraving';
          } else {
            bundledGemstoneCount += 1;
            addOnProperties[bundleLabelString] = `Gemstone ${bundledGemstoneCount}`;
            addOnProperties.Price = this.formatMoney(addOn.priceCents);
            addOnProperties._price = Number(addOn.priceCents || 0);
          }
        }

        const addOnItem = {
          id: Number(addOn.variantId),
          quantity: 1,
          properties: addOnProperties
        };

        if ( addOn.type === 'back-engraving' ) {
          backEngravingItems.push(addOnItem);
        } else {
          otherAddOnItems.push(addOnItem);
        }
      }

      // Match the PDP payload order so Shopify displays the main product before
      // its back engraving after the cart transformer builds the component group.
      for ( const backEngravingItem of backEngravingItems ) {
        items.push(backEngravingItem);
      }

      items.push(mainItem);

      for ( const otherAddOnItem of otherAddOnItems ) {
        items.push(otherAddOnItem);
      }
    }

    const cartPayload = {
      bundleId,
      items
    };

    return cartPayload;
  },
  // Regenerate personalized preview files for each main BYO product before cart submission.
  addPreviewFilesToCartItems: async function (byoSection, selections, items) {
    if ( !window.byoPersonalizationManager ) {
      return;
    }

    for ( const item of items ) {
      const slot = item.byoSourceSlot || '';
      const selection = selections[slot] || null;

      if ( !selection ) {
        continue;
      }

      const escapedSlot = CSS.escape(slot);
      const escapedProductId = CSS.escape(String(selection.productId));
      const dropdown = byoSection.querySelector(`[data-byo-dropdown][data-byo-slot="${escapedSlot}"]`);
      const productCards = dropdown?.querySelectorAll(`[data-byo-product-card][data-product-id="${escapedProductId}"]`) || [];
      let productCard = null;

      for ( const possibleCard of productCards ) {
        if ( !selection.collectionHandle || possibleCard.dataset.byoCollectionHandle === selection.collectionHandle ) {
          productCard = possibleCard;
          break;
        }
      }

      const modal = productCard ? document.getElementById(productCard.dataset.byoModalId) : null;
      const personalization = modal?.querySelector('[data-byo-personalization]');

      if ( !personalization ) {
        continue;
      }

      if ( personalization.dataset.byoPersonalizationLoaded !== 'true' ) {
        await window.byoPersonalizationManager.loadForm(personalization);
      }

      const previewFiles = await window.byoPersonalizationManager.getPreviewFiles(personalization);

      for ( const propertyName in previewFiles ) {
        item.properties[propertyName] = previewFiles[propertyName];
      }
    }
  },
  // Build the multipart cart request required for Shopify line-item file uploads.
  buildCartFormData: function (items) {
    const formData = new FormData();

    for ( let itemIndex = 0; itemIndex < items.length; itemIndex += 1 ) {
      const item = items[itemIndex];
      const itemName = `items[${itemIndex}]`;
      const properties = item.properties || {};

      formData.append(`${itemName}[id]`, item.id);
      formData.append(`${itemName}[quantity]`, item.quantity);

      for ( const propertyName in properties ) {
        const propertyValue = properties[propertyName];

        if ( propertyValue == null ) {
          continue;
        }

        const propertyKey = `${itemName}[properties][${propertyName}]`;

        if ( propertyName === 'custom_preview_front' && propertyValue instanceof Blob ) {
          formData.append(propertyKey, propertyValue, 'Front Preview.png');
        } else if ( propertyName === 'custom_preview_back' && propertyValue instanceof Blob ) {
          formData.append(propertyKey, propertyValue, 'Back Preview.png');
        } else {
          formData.append(propertyKey, String(propertyValue));
        }
      }
    }

    return formData;
  },
  // Remove every existing cart line belonging to one BYO group.
  removeCartBundle: async function (bundleId) {
    const rootUrl = window.Shopify?.routes?.root || '/';
    const cartResponse = await fetch(`${rootUrl}cart.js`);
    const cart = await cartResponse.json();
    const updates = {};

    for ( const item of cart.items || [] ) {
      if ( String(item.properties?._byo_bundle || '') === String(bundleId) ) {
        updates[item.key] = 0;
      }
    }

    let updateCount = 0;

    for (const lineKey in updates) {
      updateCount += 1;
    }

    if ( updateCount === 0 ) {
      return
    }

    const updateResponse = await fetch(`${rootUrl}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ updates })
    });

    if ( !updateResponse.ok ) {
      throw new Error(`Unable to remove the previous BYO items: ${updateResponse.status}`);
    }
  },
  // Add the complete BYO bundle to Shopify in one request.
  addBundleToCart: async function (byoSection, addBundleButton) {
    const selections = this.readSelections(byoSection);
    const editingBundleId = byoSection.dataset.byoEditingBundle || '';
    const byoPagePath = byoSection.dataset.byoPagePath || window.location.pathname;
    const byoCartTitle = byoSection.dataset.byoCartTitle || 'Build Your Own';
    const cartPayload = this.buildCartItems(selections, editingBundleId, byoPagePath, byoCartTitle);
    const items = cartPayload.items;
    let cartUpdated = false;

    if ( items.length === 0 || addBundleButton.disabled ) {
      return
    }

    addBundleButton.disabled = true;
    addBundleButton.setAttribute('aria-busy', 'true');

    try {
      await this.addPreviewFilesToCartItems(byoSection, selections, items);
      const formData = this.buildCartFormData(items);

      if ( editingBundleId ) {
        await this.removeCartBundle(editingBundleId);
      }

      const cartUrl = `${window.Shopify?.routes?.root || '/'}cart/add.js`;
      const response = await fetch(cartUrl, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        body: formData
      });

      if ( !response.ok ) {
        throw new Error(`Unable to add bundle to cart: ${response.status}`);
      }

      this.writeBundleSelections(cartPayload.bundleId, selections);
      cartUpdated = true;

      if ( editingBundleId ) {
        byoSection.removeAttribute('data-byo-editing-bundle');
        this.clearEditUrl();

        const buttonLabel = byoSection.querySelector('[data-byo-atc-label]');

        if ( buttonLabel ) {
          buttonLabel.textContent = buttonLabel.dataset.defaultLabel;
        }
      }

      this.resetSection(byoSection);
      this.refreshLoadedPersonalizationForms(byoSection);

      document.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }));
      document.dispatchEvent(new CustomEvent('cart:open', {
        bubbles: true
      }));
    } catch (error) {
      console.error(error);
      addBundleButton.disabled = false;
    } finally {
      addBundleButton.removeAttribute('aria-busy');

      if ( !cartUpdated ) {
        this.updateBundleTotal(byoSection, selections);
      }
    }
  },
  // Remove the edit query parameter without reloading the page.
  clearEditUrl: function () {
    const url = new URL(window.location.href);
    url.searchParams.delete('edit_byo_bundle');
    window.history.replaceState({}, document.title, url.toString());
  },
  // Restore a saved cart group and switch the final button to update mode.
  continueEditing: function (byoSection, editModal, bundleId) {
    const selections = this.readBundleSelections(bundleId);
    let selectionCount = 0;

    for (const slot in selections) {
      selectionCount += 1;
    }

    if ( selectionCount === 0 ) {
      return
    }

    // this.resetSection(byoSection);
    this.writeSelections(byoSection, selections);
    const openedDropdown = this.restoreSelections(byoSection, true);
    byoSection.dataset.byoEditingBundle = bundleId;

    const buttonLabel = byoSection.querySelector('[data-byo-atc-label]');

    if ( buttonLabel ) {
      buttonLabel.textContent = buttonLabel.dataset.updateLabel;
    }

    this.refreshLoadedPersonalizationForms(byoSection);

    const hidePromise = editModal.hide();

    if ( openedDropdown ) {
      Promise.resolve(hidePromise).then(() => {
        window.requestAnimationFrame(() => {
          this.syncDropdownChainColorImages(byoSection, openedDropdown);
        });
      });
    }
  },
  // Show or clear the loading state while Continue waits for the full page load.
  setEditContinueLoading: function (continueButton, isLoading) {
    continueButton.disabled = isLoading;

    if ( isLoading ) {
      continueButton.setAttribute('aria-busy', 'true');
    } else {
      continueButton.removeAttribute('aria-busy');
    }
  },
  // Continue immediately when loaded, or wait with a spinner until page resources finish.
  continueEditingWhenLoaded: function (byoSection, editModal, bundleId, continueButton) {
    if ( document.readyState === 'complete' ) {
      this.continueEditing(byoSection, editModal, bundleId);
      this.setEditContinueLoading(continueButton, false);
      continueButton.removeAttribute('data-byo-waiting-for-load');
      continueButton.removeAttribute('data-byo-continue-requested');
      return;
    }

    if ( continueButton.dataset.byoWaitingForLoad === 'true' ) {
      return;
    }

    continueButton.dataset.byoWaitingForLoad = 'true';
    this.setEditContinueLoading(continueButton, true);

    const finishEditing = () => {
      try {
        this.continueEditing(byoSection, editModal, bundleId);
      } finally {
        this.setEditContinueLoading(continueButton, false);
        continueButton.removeAttribute('data-byo-waiting-for-load');
        continueButton.removeAttribute('data-byo-continue-requested');
      }
    };

    window.addEventListener('load', finishEditing, { once: true });

    if ( document.readyState === 'complete' ) {
      window.removeEventListener('load', finishEditing);
      finishEditing();
    }
  },
  // Rebuild open or previously loaded personalized modals from the active saved selections.
  refreshLoadedPersonalizationForms: function (byoSection) {
    if ( !window.byoPersonalizationManager ) {
      return
    }

    const personalizationForms = byoSection.querySelectorAll('[data-byo-personalization][data-byo-personalization-loaded="true"]');

    for ( const personalization of personalizationForms ) {
      window.byoPersonalizationManager.buildForm(personalization);
      window.byoPersonalizationManager.restoreSavedForm(personalization);
    }
  },
  // Start a separate BYO group and leave the existing cart group unchanged.
  startNewBundle: function (byoSection, editModal) {
    this.resetSection(byoSection);
    byoSection.removeAttribute('data-byo-editing-bundle');

    const buttonLabel = byoSection.querySelector('[data-byo-atc-label]');

    if ( buttonLabel ) {
      buttonLabel.textContent = buttonLabel.dataset.defaultLabel;
    }

    this.refreshLoadedPersonalizationForms(byoSection);

    this.clearEditUrl();
    editModal.hide();
  },
  // Show the edit warning when the customer follows a cart drawer Edit link.
  initializeEditMode: function (byoSection) {
    const requestedBundleId = this.getRequestedEditingBundleId();
    const bundleId = requestedBundleId;

    if ( !bundleId ) {
      return
    }

    const editModal = document.querySelector('[data-byo-edit-modal]');
    const continueButton = editModal?.querySelector('[data-byo-edit-continue]');
    const newButton = editModal?.querySelector('[data-byo-edit-new]');

    if ( !editModal || !continueButton || !newButton ) {
      return
    }

    editModal.dataset.byoManagerReady = 'true';

    continueButton.addEventListener('click', () => {
      this.continueEditingWhenLoaded(byoSection, editModal, bundleId, continueButton);
    });
    newButton.addEventListener('click', () => {
      this.startNewBundle(byoSection, editModal);
    });

    if ( continueButton.dataset.byoContinueRequested === 'true' ) {
      this.continueEditingWhenLoaded(byoSection, editModal, bundleId, continueButton);
    }

    const showEditModal = () => {
      if ( editModal.dataset.byoEditModalShown === 'true' ) {
        return;
      }

      editModal.dataset.byoEditModalShown = 'true';
      editModal.show();
    };

    if ( window.customElements.get('x-modal') ) {
      showEditModal();
    } else {
      window.customElements.whenDefined('x-modal').then(showEditModal);
    }
  },
  // Let mouse users drag overflowing collection navigation on mobile and tablet layouts.
  initializeCollectionNavigationDragging: function (byoSection) {
    const navigations = byoSection.querySelectorAll('.byo-collection-snippet__navigation');

    for ( const navigation of navigations ) {
      if ( navigation.dataset.byoDragReady === 'true' ) {
        continue;
      }

      navigation.dataset.byoDragReady = 'true';
      let pointerId = null;
      let startX = 0;
      let startScrollLeft = 0;
      let didDrag = false;

      navigation.addEventListener('pointerdown', (event) => {
        const isDesktopLayout = window.matchMedia('(min-width: 1000px)').matches;

        if ( isDesktopLayout || event.pointerType !== 'mouse' || event.button !== 0 || navigation.scrollWidth <= navigation.clientWidth ) {
          return;
        }

        pointerId = event.pointerId;
        startX = event.clientX;
        startScrollLeft = navigation.scrollLeft;
        didDrag = false;
        navigation.setPointerCapture(pointerId);
      });

      navigation.addEventListener('pointermove', (event) => {
        if ( pointerId !== event.pointerId ) {
          return;
        }

        const distance = event.clientX - startX;

        if ( Math.abs(distance) > 5 ) {
          didDrag = true;
          navigation.classList.add('is-dragging');
        }

        if ( didDrag ) {
          event.preventDefault();
          navigation.scrollLeft = startScrollLeft - distance;
        }
      });

      const finishDragging = (event) => {
        if ( pointerId !== event.pointerId ) {
          return;
        }

        if ( navigation.hasPointerCapture(pointerId) ) {
          navigation.releasePointerCapture(pointerId);
        }

        pointerId = null;
        navigation.classList.remove('is-dragging');
      };

      navigation.addEventListener('pointerup', finishDragging);
      navigation.addEventListener('pointercancel', finishDragging);
      navigation.addEventListener('click', (event) => {
        if ( !didDrag ) {
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        didDrag = false;
      }, true);
    }
  },
  // Initialize every BYO section and attach its delegated interaction handlers.
  init: function () {
    const byoSections = document.querySelectorAll('[data-byo-section]');


    for (const byoSection of byoSections) {
      if ( byoSection.dataset.byoReady === 'true' ) {
        continue;
      }

      byoSection.dataset.byoReady = 'true';

      const formColorMatch = byoSection.getAttribute('data-force-charm-colors-to-match-chain') === 'true';

      this.initializeEditMode(byoSection);

      this.syncReviewPanel(byoSection);
      this.initializeCollectionNavigationDragging(byoSection);

      byoSection.addEventListener('click', (event) => {
        const addBundleButton = event.target.closest('[data-byo-add-bundle-cart]');

        if ( addBundleButton && byoSection.contains(addBundleButton) ) {
          this.addBundleToCart(byoSection, addBundleButton);
          return
        }

        const reviewOpenButton = event.target.closest('[data-byo-review-open]');

        if ( reviewOpenButton && byoSection.contains(reviewOpenButton) ) {
          this.openReviewModal(byoSection, reviewOpenButton.dataset.byoReviewOpen);
          return
        }

        const reviewRemoveButton = event.target.closest('[data-byo-review-remove]');

        if ( reviewRemoveButton && byoSection.contains(reviewRemoveButton) ) {
          const slot = CSS.escape(reviewRemoveButton.dataset.byoReviewRemove);
          const dropdown = byoSection.querySelector(`[data-byo-dropdown][data-byo-slot="${slot}"]`);

          if ( dropdown ) {
            this.clearSelection(byoSection, dropdown);
          }
          return
        }

        const removeButton = event.target.closest('[data-byo-remove]');

        if ( removeButton && byoSection.contains(removeButton) ) {
          this.clearSelection(byoSection, removeButton.closest('[data-byo-dropdown]'));
          return
        }

        const resetButton = event.target.closest('[data-byo-reset]');

        if ( resetButton && byoSection.contains(resetButton) ) {
          this.resetSection(byoSection);
          return
        }

        const toggle = event.target.closest('[data-byo-toggle]');

        if ( toggle && byoSection.contains(toggle) ) {
          const panel = document.getElementById(toggle.getAttribute('aria-controls'));
          const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
          const dropdown = toggle.closest('[data-byo-dropdown]');

          if ( !panel || !byoSection.contains(panel) ) {
            return
          }

          if ( !isExpanded && dropdown ) {
            this.syncDropdownChainColorImages(byoSection, dropdown);
          }

          toggle.setAttribute('aria-expanded', String(!isExpanded));
          panel.hidden = isExpanded;
          return
        }

        if ( event.target.closest('x-modal') ) {
          return
        }

        const productCard = event.target.closest('[data-byo-product-card]');

        if ( !productCard || !byoSection.contains(productCard) || productCard.getAttribute('aria-disabled') === 'true' ) {
          return
        }

        const isExplicitTrigger = event.target.closest('[data-byo-product-add], .product-title, .product-card__media');
        const isOtherInteractiveElement = event.target.closest('a, button, input, label, select, textarea');

        if ( !isExplicitTrigger && isOtherInteractiveElement ) {
          return
        }

        const modal = document.getElementById(productCard.dataset.byoModalId);
        const dropdown = productCard.closest('[data-byo-dropdown]');
        const selections = this.readSelections(byoSection);
        const savedSelection = selections[dropdown?.dataset.byoSlot] || null;

        if ( savedSelection && String(savedSelection.productId) === String(productCard.dataset.productId) ) {
          this.selectCardVariant(productCard, savedSelection.variantId);
        } else {
          if ( formColorMatch ) {
            const chainColor = selections.chain?.selectedColor || '';
            this.preselectCardColor(productCard, chainColor);
          }
        }

        this.updateModalActionLabel(productCard, modal);

        if ( modal ) {
          const showPromise = modal.show();

          showPromise.then(() => {
            if ( savedSelection && String(savedSelection.productId) === String(productCard.dataset.productId) ) {
              this.selectCardVariant(productCard, savedSelection.variantId);
            } else {
              if ( formColorMatch ) {
                const chainColor = selections.chain?.selectedColor || '';
                this.preselectCardColor(productCard, chainColor);
              }
            }

            this.updateModalActionLabel(productCard, modal);
          });
        }
      });
    }
  }
};

const reviewBreakpoint = window.matchMedia('(min-width: 1000px)');
const handleReviewBreakpointChange = () => {
  const byoSections = document.querySelectorAll('[data-byo-section]');

  for (const byoSection of byoSections) {
    buildYourOwnManager.syncReviewPanel(byoSection);
  }
};

document.addEventListener('click', (event) => {
  buildYourOwnManager.handleAddToBundle(event);
});

document.addEventListener('byo:personalization-ready', (event) => {
  const modal = event.target.closest('x-modal');
  const productCardId = modal?.dataset.byoOriginCardId;
  const productCard = productCardId ? document.getElementById(productCardId) : null;

  if ( productCard && modal ) {
    buildYourOwnManager.updateModalActionLabel(productCard, modal);
  }
});

document.addEventListener('shopify:section:load', () => {
  buildYourOwnManager.init();
});

document.addEventListener('DOMContentLoaded', () => {
  buildYourOwnManager.init();
});

buildYourOwnManager.init();

if ( reviewBreakpoint.addEventListener ) {
  reviewBreakpoint.addEventListener('change', handleReviewBreakpointChange);
} else {
  reviewBreakpoint.addListener(handleReviewBreakpointChange);
}
